<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import axios from 'axios';
import { useField, useForm } from 'vee-validate';
import * as zod from 'zod';

import { useAlertsStore } from '~/stores/alerts/useAlertsStore';

const { common } = useEVMApi();
const alertStore = useAlertsStore();

const emit = defineEmits(['closeModal']);

const validationSchema = toTypedSchema(zod.object({
    email: zod.string().min(
        1,
        { message: 'This is required' }
    )
        .email({ message: 'Must be a valid email' }),
    name: zod.string().min(
        1,
        { message: 'This is required' }
    )
        .min(
            3,
            { message: 'Name must be at least 3 characters' }
        )
}));
const {
    handleSubmit,
    setErrors,
    meta
} = useForm({
    validationSchema
});

const {
    'value': email,
    'errorMessage': emailError
} = useField('email');
const {
    'value': name,
    'errorMessage': nameError
} = useField('name');

const closeModal = () => {
    emit('closeModal');
};

const isLoading = ref(false);

const onSubmit = handleSubmit(async (values: Record<string, string>) => {
    isLoading.value = true;
    try {
        const response = await common.subscribe(
            email.value as string,
            name.value as string
        );
        console.log(
            'Subscription successful:',
            response
        );
        alertStore.add({
            title: 'Success',
            description: 'Subscription successful!',
            critical: false,
            status: 'type.success'
        });
        closeModal();
    }
    catch (error) {
        if (axios.isAxiosError(error)) {
            console.error(
                'Failed to subscribe:',
                error
            );
            if (error.response && error.response.status === 409) {
                setErrors({
                    email: error.response.data.status.message
                });
            }
            else {
                alertStore.add({
                    title: 'Error',
                    description: 'Subscription failed!',
                    critical: false,
                    status: 'type.error'
                });
            }
        }
        else {
            console.error('An unknown error occurred:', error);
            alertStore.add({
                title: 'Error',
                description: 'An unknown error occurred!',
                critical: false,
                status: 'type.error'
            });
        }
    }
    finally {
        isLoading.value = false;
    }
    console.log(
        'submit',
        values
    );
});

</script>

<template>
  <div class="make-offer newsletter display-flex flex-column align-items-start position-relative gap-24">
    <div class="display-flex width-full align-items-center justify-center flex-column">
      <p class="display-800-accent">
        Newsletter
      </p>
      <p class="text-700-default news-subtitle">
        Become a subscriber and don’t miss latest updates!
      </p>
    </div>

    <form class="width-full display-flex flex-column gap-24">
      <div class="width-full display-flex flex-column gap-8">
        <input v-model="email"
               type="email"
               aria-label="email"
               placeholder="Email"
               name="email"
               class="input-main compact-500-accent"
        >
        <span v-if="emailError"
              class="input-error"
        >{{ emailError }}</span>

        <input v-model="name"
               type="text"
               aria-label="name"
               placeholder="Name"
               name="name"
               class="input-main compact-500-accent text-color-secondary"
        >
        <span v-if="nameError"
              class="input-error"
        >{{ nameError }}</span>
      </div>

      <div v-show="false"
           class="display-flex flex-column width-full gap-8"
      >
        <p class="compact-500-accent">
          Subscribe to
        </p>

        <div class="display-flex flex-column gap-8 width-full">
          <div class="display-flex align-items-center justify-between">
            <div class="display-flex gap-16 align-items-center">
              <IconMain icon="metamask"
                        :size="36"
              />
              <p class="compact-400-accent">
                STEPN
              </p>
            </div>
            <Checkbox label="STEPN" />
          </div>

          <div class="display-flex align-items-center justify-between">
            <div class="display-flex gap-16 align-items-center">
              <IconMain icon="metamask"
                        :size="36"
              />
              <p class="compact-400-accent">
                Gas Hero
              </p>
            </div>
            <Checkbox label="Gas Hero" />
          </div>
        </div>
      </div>
      <Button size="size.medium"
              color="bg.mojito"
              class="width-full"
              :is-loading="isLoading"
              :is-disabled="!meta.valid"
      >
        <button class="compact-500-accent"
                type="submit"
                @click="onSubmit"
        >
          Subscribe
        </button>
        <IconMain :size="24"
                  icon="arrow-r-l"
                  class="mr-8"
        />
      </Button>
    </form>
  </div>
</template>

  <style lang="scss">

  .make-offer {
    box-sizing: border-box;
    width: 100%;
    max-width: 600px;
    padding: 40px;
    border-radius: var(--background-500-radius, 4px);
    background: var(--layer-floor-2);
  }

  .newsletter {
    width: 100%;
    max-width: 740px;
  }

  .news-subtitle {
    color: var(--content-secondary);
    text-align: center;
  }

  .no-scroll {
    overflow: hidden;
  }

  .input-error {
    color: var(--content-negative);
    font-size: 14px;
  }
  </style>
